/**
 * Service worker registration for the MANGA PWA.
 *
 * Wave 1 PR-2 — uses vite-plugin-pwa's virtual entry point so the build
 * pipeline injects the correct service worker URL (which is hashed in
 * production).
 *
 * registerType: 'autoUpdate' in vite.config.ts means we don't have to
 * prompt the user to update — the next page reload after a deploy
 * activates the new SW. For Wave 1 that's the right tradeoff: vaqueros
 * are not going to interpret "update available" toasts.
 */

export function registerServiceWorker(): void {
  // Dynamic import + try/catch so a missing virtual module in dev mode
  // doesn't crash the bundle. The plugin's devOptions.enabled = false
  // means /sw.js is only emitted in production builds.
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;

  // virtual:pwa-register is provided at build time by vite-plugin-pwa.
  // The dynamic-import comment hint keeps Vite from trying to resolve it
  // when the plugin isn't loaded (e.g. unit tests).
  import(/* @vite-ignore */ 'virtual:pwa-register')
    .then((mod) => {
      const registerSW = mod.registerSW as (opts?: {
        immediate?: boolean;
        onNeedRefresh?: () => void;
        onOfflineReady?: () => void;
        onRegisteredSW?: (url: string, reg: ServiceWorkerRegistration | undefined) => void;
        onRegisterError?: (err: unknown) => void;
      }) => (reloadPage?: boolean) => Promise<void>;

      // 3.9q — capture the updateSW handle so we can force-reload the
      // page the moment a new bundle is detected. With skipWaiting +
      // clientsClaim on (vite.config.ts), the new SW will already be
      // controlling the page when onNeedRefresh fires; calling
      // updateSW(true) just guarantees the React tree boots against the
      // new bundle's hashed chunks instead of half-old half-new code.
      const updateSW = registerSW({
        immediate: true,
        onOfflineReady() {
          // The vaquero can now use the app fully offline. We log only —
          // no toast, no banner. The PR-2 install banner already gives
          // them one moment of context.
          console.log('[pwa] offline ready');
        },
        onNeedRefresh() {
          console.log('[pwa] new content active — reloading');
          // Hard-reload into the new bundle. Without this, the vaquero
          // keeps running the old JS until they happen to navigate, and
          // any new server-side contract (a new event tipo, a new field)
          // will crash the stale client. We accept the one-time blip;
          // it's cheaper than the AdminShell→VaqueroShell r.bg crash.
          updateSW(true).catch((e) => console.warn('[pwa] reload failed', e));
        },
        onRegisteredSW(url) {
          console.log('[pwa] sw registered at', url);
        },
        onRegisterError(err) {
          console.error('[pwa] sw registration failed:', err);
        },
      });

      // 3.9q — belt and suspenders: when the SW that controls this page
      // changes (e.g. clientsClaim activates a new one mid-session),
      // reload once so the React bundle in memory matches the chunks the
      // new SW will serve. Guarded by a one-shot flag so we don't loop.
      let reloaded = false;
      navigator.serviceWorker?.addEventListener('controllerchange', () => {
        if (reloaded) return;
        reloaded = true;
        console.log('[pwa] controller changed — reloading once');
        window.location.reload();
      });
    })
    .catch((err) => {
      // Dev mode or test runner — virtual module not available.
      console.debug('[pwa] sw module not available (likely dev):', (err as Error).message);
    });
}
